import React, { useCallback, useEffect, useState } from 'react';
import { API_CONFIG } from '../config';
import { ICONS } from '../constants';
import { useTranslation } from '../hooks/useTranslation';
import Button from './ui/Button';
import ConfirmationModal from './ui/ConfirmationModal';

interface MediaFileEntry {
  path: string;
  size: number;
  movedAt?: string;
  batchId?: string;
}

interface FileListResponse {
  files: MediaFileEntry[];
  count: number;
  totalBytes: number;
  totalMB: number;
  batches?: Array<{ id: string; movedAt: string; count: number; totalBytes: number }>;
}

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

function authHeaders(): HeadersInit {
  const token = localStorage.getItem('spellbook_token');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

const MediaTrashBinPanel: React.FC = () => {
  const { t } = useTranslation();
  const [orphans, setOrphans] = useState<FileListResponse | null>(null);
  const [trash, setTrash] = useState<FileListResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedOrphans, setSelectedOrphans] = useState<Set<string>>(new Set());
  const [selectedTrash, setSelectedTrash] = useState<Set<string>>(new Set());
  const [view, setView] = useState<'orphans' | 'trash'>('orphans');
  const [confirmEmptyOpen, setConfirmEmptyOpen] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [previewRes, trashRes] = await Promise.all([
        fetch(`${API_CONFIG.API_URL}/backup/media-trash/preview`, { headers: authHeaders() }),
        fetch(`${API_CONFIG.API_URL}/backup/media-trash`, { headers: authHeaders() }),
      ]);

      if (!previewRes.ok || !trashRes.ok) {
        const errBody = !previewRes.ok
          ? await previewRes.json().catch(() => ({}))
          : await trashRes.json().catch(() => ({}));
        throw new Error(errBody.error || `HTTP ${previewRes.status}/${trashRes.status}`);
      }

      const previewData = (await previewRes.json()) as FileListResponse;
      const trashData = (await trashRes.json()) as FileListResponse;
      setOrphans(previewData);
      setTrash(trashData);
      setSelectedOrphans(new Set());
      setSelectedTrash(new Set());
    } catch (err) {
      setError(err instanceof Error ? err.message : t('system.backup.connectionError'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const togglePath = (set: Set<string>, path: string, setter: (s: Set<string>) => void) => {
    const next = new Set(set);
    if (next.has(path)) next.delete(path);
    else next.add(path);
    setter(next);
  };

  const selectAll = (files: MediaFileEntry[], setter: (s: Set<string>) => void) => {
    setter(new Set(files.map((f) => f.path)));
  };

  const handleMove = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${API_CONFIG.API_URL}/backup/media-trash/move`, {
        method: 'POST',
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('system.backup.trashMoveFailed'));
      setSuccess(
        t('system.backup.trashMoveSuccess')
          .replace('{count}', String(data.count))
          .replace('{size}', formatBytes(data.totalBytes || 0))
      );
      setView('trash');
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('system.backup.trashMoveFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async (all: boolean) => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const body = all
        ? { all: true }
        : { paths: Array.from(selectedTrash) };
      if (!all && (body as { paths: string[] }).paths.length === 0) {
        setError(t('system.backup.trashSelectRestore'));
        setLoading(false);
        return;
      }
      const res = await fetch(`${API_CONFIG.API_URL}/backup/media-trash/restore`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('system.backup.trashRestoreFailed'));
      setSuccess(
        t('system.backup.trashRestoreSuccess')
          .replace('{count}', String(data.count))
          .replace('{skipped}', String(data.skipped?.length || 0))
      );
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('system.backup.trashRestoreFailed'));
    } finally {
      setLoading(false);
    }
  };

  const handleEmpty = async () => {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const res = await fetch(`${API_CONFIG.API_URL}/backup/media-trash/empty`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ confirm: true }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || t('system.backup.trashEmptyFailed'));
      setSuccess(
        t('system.backup.trashEmptySuccess')
          .replace('{count}', String(data.deletedCount))
          .replace('{size}', formatBytes(data.totalBytes || 0))
      );
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : t('system.backup.trashEmptyFailed'));
    } finally {
      setLoading(false);
      setConfirmEmptyOpen(false);
    }
  };

  const activeList = view === 'orphans' ? orphans : trash;
  const selected = view === 'orphans' ? selectedOrphans : selectedTrash;
  const setSelected = view === 'orphans' ? setSelectedOrphans : setSelectedTrash;

  const selectedBytes =
    activeList?.files
      .filter((f) => selected.has(f.path))
      .reduce((sum, f) => sum + (f.size || 0), 0) || 0;

  return (
    <div className="bg-gray-700 rounded-lg p-6 space-y-4">
      <h3 className="text-lg font-semibold text-white mb-1 flex items-center">
        <ICONS.Trash className="w-5 h-5 mr-2" />
        {t('system.backup.trashTitle')}
      </h3>
      <p className="text-sm text-gray-300">{t('system.backup.trashHint')}</p>
      <p className="text-xs text-amber-300">{t('system.backup.trashPhaseHint')}</p>

      <div className="grid grid-cols-2 gap-3 text-sm">
        <div className="bg-gray-800 rounded-lg p-3">
          <div className="text-gray-400">{t('system.backup.trashOrphans')}</div>
          <div className="text-white font-mono text-lg">
            {orphans?.count ?? '—'} · {orphans ? formatBytes(orphans.totalBytes) : '—'}
          </div>
        </div>
        <div className="bg-gray-800 rounded-lg p-3">
          <div className="text-gray-400">{t('system.backup.trashInBin')}</div>
          <div className="text-white font-mono text-lg">
            {trash?.count ?? '—'} · {trash ? formatBytes(trash.totalBytes) : '—'}
          </div>
        </div>
      </div>

      {error && (
        <div className="text-sm text-red-300 bg-red-900/30 border border-red-700 rounded p-2">{error}</div>
      )}
      {success && (
        <div className="text-sm text-green-300 bg-green-900/30 border border-green-700 rounded p-2">{success}</div>
      )}

      <div className="flex flex-nowrap gap-2 overflow-x-auto">
        <Button
          variant={view === 'orphans' ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => setView('orphans')}
          className="bg-gray-600 whitespace-nowrap shrink-0"
        >
          {t('system.backup.trashViewOrphans')}
        </Button>
        <Button
          variant={view === 'trash' ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => setView('trash')}
          className="bg-gray-600 whitespace-nowrap shrink-0"
        >
          {t('system.backup.trashViewBin')}
        </Button>
        <Button variant="secondary" size="sm" onClick={refresh} disabled={loading} className="whitespace-nowrap shrink-0">
          <ICONS.RefreshCw className="w-4 h-4 mr-1" />
          {t('system.backup.trashRefresh')}
        </Button>
      </div>

      <div className="flex flex-nowrap gap-2 overflow-x-auto">
        {view === 'orphans' && (
          <Button
            variant="primary"
            onClick={handleMove}
            disabled={loading || !orphans?.count}
            className="bg-indigo-600 hover:bg-indigo-700 whitespace-nowrap shrink-0"
          >
            {t('system.backup.trashMove')}
          </Button>
        )}
        {view === 'trash' && (
          <>
            <Button
              variant="secondary"
              onClick={() => handleRestore(false)}
              disabled={loading || selectedTrash.size === 0}
              className="whitespace-nowrap shrink-0"
            >
              {t('system.backup.trashRestoreSelected')}
            </Button>
            <Button
              variant="secondary"
              onClick={() => handleRestore(true)}
              disabled={loading || !trash?.count}
              className="whitespace-nowrap shrink-0"
            >
              {t('system.backup.trashRestoreAll')}
            </Button>
            <Button
              variant="danger"
              onClick={() => setConfirmEmptyOpen(true)}
              disabled={loading || !trash?.count}
              className="whitespace-nowrap shrink-0"
            >
              {t('system.backup.trashEmpty')}
            </Button>
          </>
        )}
      </div>

      {selected.size > 0 && (
        <p className="text-xs text-gray-400">
          {t('system.backup.trashSelected')
            .replace('{count}', String(selected.size))
            .replace('{size}', formatBytes(selectedBytes))}
        </p>
      )}

      <div className="max-h-64 overflow-auto border border-gray-600 rounded">
        <table className="w-full text-xs text-left">
          <thead className="bg-gray-800 sticky top-0 text-gray-300">
            <tr>
              <th className="p-2 w-8">
                <input
                  type="checkbox"
                  checked={
                    !!activeList?.files.length &&
                    activeList.files.every((f) => selected.has(f.path))
                  }
                  onChange={(e) => {
                    if (!activeList) return;
                    if (e.target.checked) selectAll(activeList.files, setSelected);
                    else setSelected(new Set());
                  }}
                />
              </th>
              <th className="p-2">{t('system.backup.trashColPath')}</th>
              <th className="p-2 w-24 text-right">{t('system.backup.trashColSize')}</th>
            </tr>
          </thead>
          <tbody className="text-gray-200">
            {loading && !activeList && (
              <tr>
                <td colSpan={3} className="p-3 text-center text-gray-400">
                  {t('system.backup.trashLoading')}
                </td>
              </tr>
            )}
            {activeList?.files.length === 0 && (
              <tr>
                <td colSpan={3} className="p-3 text-center text-gray-400">
                  {view === 'orphans'
                    ? t('system.backup.trashNoOrphans')
                    : t('system.backup.trashEmptyBin')}
                </td>
              </tr>
            )}
            {activeList?.files.map((file) => (
              <tr key={file.path} className="border-t border-gray-600 hover:bg-gray-800/60">
                <td className="p-2">
                  <input
                    type="checkbox"
                    checked={selected.has(file.path)}
                    onChange={() => togglePath(selected, file.path, setSelected)}
                  />
                </td>
                <td className="p-2 font-mono break-all">{file.path}</td>
                <td className="p-2 text-right font-mono whitespace-nowrap">
                  {formatBytes(file.size)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ConfirmationModal
        isOpen={confirmEmptyOpen}
        onClose={() => setConfirmEmptyOpen(false)}
        onConfirm={handleEmpty}
        type="danger"
        title={t('system.backup.trashEmptyConfirmTitle')}
        message={t('system.backup.trashEmptyConfirmMessage')
          .replace('{count}', String(trash?.count || 0))
          .replace('{size}', trash ? formatBytes(trash.totalBytes) : '0')}
        confirmText={t('system.backup.trashEmpty')}
      />
    </div>
  );
};

export default MediaTrashBinPanel;
